import { useState } from 'react';
import { Ruler, Scale } from 'lucide-react';
import { getWeightOn, upsertWeight, weightTrend, weightVerdict } from '../body';
import { measurementSummary } from '../logic/measurements';
import { MeasureSheet } from '../body/MeasureSheet';
import { GoalLine } from '../body/WeightGoal';
import { weightGoalProgress } from '../logic/goal';
import { FONT_BODY, FONT_MONO } from '../theme';
import { Card, NumberInput } from '../ui';

/* One number a day, next to the supplements. Leads with the 7-day average
 * rather than today's reading, because that's the one worth reacting to. */
export function WeightCheckIn({ t, state, setState, dayKey }) {
  const metrics = state.bodyMetrics || [];
  const value = getWeightOn(metrics, dayKey);
  const trend = weightTrend(metrics, dayKey);
  const verdict = weightVerdict(trend && trend.perWeek, state.profile?.goal);
  const perWeek = trend && trend.perWeek;
  const col = verdict === 'good' ? t.accent : verdict === 'bad' ? t.danger : t.textMuted;

  const [measureOpen, setMeasureOpen] = useState(false);
  const tape = measurementSummary(state, dayKey);
  const goal = weightGoalProgress(state, dayKey);

  const save = (w) => setState(prev => ({
    ...prev, bodyMetrics: upsertWeight(prev.bodyMetrics, dayKey, w),
  }));

  return (
    <Card t={t} padding={14}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <Scale size={15} color={t.textFaint} />
          <span style={{ fontSize: 13, fontWeight: 700, color: t.text }}>Weight</span>
        </div>

        <div style={{ width: 118, flexShrink: 0 }}>
          <NumberInput t={t} value={value} onChange={save} step={0.1} min={0} suffix="kg" placeholder="–" />
        </div>

        {trend && trend.avg != null && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, minWidth: 0, marginLeft: 'auto' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10, color: t.textFaint, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                7-day avg
              </div>
              <div style={{ fontSize: 15, fontFamily: FONT_MONO, fontWeight: 700, color: t.text }}>
                {trend.avg.toFixed(1)}<span style={{ fontSize: 10, fontWeight: 400, color: t.textMuted }}>kg</span>
              </div>
            </div>
            {perWeek != null && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: t.textFaint, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  / week
                </div>
                <div style={{ fontSize: 15, fontFamily: FONT_MONO, fontWeight: 700, color: col }}>
                  {perWeek > 0 ? '+' : ''}{perWeek.toFixed(1)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <GoalLine t={t} p={goal} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
        <button type="button" onClick={() => setMeasureOpen(true)} style={{
          display: 'flex', alignItems: 'center', gap: 6, minHeight: 36, padding: '0 10px', borderRadius: 8,
          background: 'transparent', border: `1px solid ${t.cardBorder}`, color: t.textMuted, cursor: 'pointer',
          fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700,
        }}>
          <Ruler size={13} /> Measure
        </button>
        {tape.due && (
          <span style={{ fontSize: 11, color: t.textFaint, fontFamily: FONT_MONO }}>Tape: {tape.daysSince} d ago</span>
        )}
      </div>

      {measureOpen && <MeasureSheet t={t} state={state} setState={setState} dayKey={dayKey} onClose={() => setMeasureOpen(false)} />}

      {trend && trend.avg != null && perWeek == null && (
        <div style={{ fontSize: 11, color: t.textFaint, marginTop: 8, lineHeight: 1.45 }}>
          Keep weighing in — the weekly trend needs about two weeks of readings before it means anything.
        </div>
      )}
    </Card>
  );
}
